/**
 * Canal singleton para la "energía" actual del orb (RMS combinado del mic
 * en `listening` o del TTS player en `speaking`). Se publica desde
 * `useVoiceSession` cuando arranca la sesión y se limpia al cerrarla.
 *
 * El consumidor (componente `Orb`) lee este getter desde un RAF loop y
 * escribe el valor como CSS var `--orb-energy` directamente en el DOM,
 * sin disparar re-renders por frame en React.
 *
 * Hay como mucho UNA voice session activa en el cliente — los singletons
 * de `MicCapture` y `AudioPlayer` ya asumen esto. Por eso el canal es un
 * module-level state, evita prop drilling por 5 capas del shell.
 *
 * Si nadie publicó un getter (sesión idle), `getOrbEnergy()` devuelve 0.
 */

let currentGetter: (() => number) | null = null;

export function setOrbEnergyGetter(getter: (() => number) | null): void {
  currentGetter = getter;
}

export function getOrbEnergy(): number {
  if (!currentGetter) return 0;
  try {
    return currentGetter();
  } catch {
    return 0;
  }
}

/**
 * Indica si el bot ya empezó a responder (al menos un token del LLM
 * llegó al `assistantText` del turno actual). Usado por el Orb para
 * apagar el ring rotante de `thinking` apenas hay texto visible, aunque
 * el audio TTS todavía no haya empezado a sonar.
 *
 * Devuelve `false` si nadie publicó un getter o si la respuesta aún no
 * arrancó.
 */
let responseStartedGetter: (() => boolean) | null = null;

export function setOrbResponseStartedGetter(getter: (() => boolean) | null): void {
  responseStartedGetter = getter;
}

export function getOrbResponseStarted(): boolean {
  if (!responseStartedGetter) return false;
  try {
    return responseStartedGetter();
  } catch {
    return false;
  }
}

/**
 * `true` si el AudioPlayer todavía tiene audio TTS in-flight (cola o
 * sonando). El server emite `state: listening` apenas termina de MANDAR
 * los chunks, pero los chunks siguen sonando varios cientos de ms en el
 * cliente. La orb usa este flag para forzar el visual de `speaking`
 * durante ese gap — sino aparece "escuchando" mientras la AI sigue
 * hablando, lo que es visualmente inconsistente.
 */
let audioPlayingGetter: (() => boolean) | null = null;

export function setOrbAudioPlayingGetter(getter: (() => boolean) | null): void {
  audioPlayingGetter = getter;
}

export function getOrbAudioPlaying(): boolean {
  if (!audioPlayingGetter) return false;
  try {
    return audioPlayingGetter();
  } catch {
    return false;
  }
}
